// RekapTransaksiBantuan.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import {
  Box, Card, CardContent, Typography, Chip, CircularProgress, Alert,
  Snackbar, Fade, Button, Stack, Grid, useMediaQuery, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails, TextField, MenuItem,
  FormControl, InputLabel, Select, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, LinearProgress, Divider,
  List, ListItem, ListItemText, ListItemIcon, Badge
} from '@mui/material';
import {
  Refresh, Analytics, Download, NavigateBefore, NavigateNext,
  HomeWork, Payments, CheckCircle, ExpandMore, Store,
  AttachMoney, TrendingUp, TrendingDown, Receipt,
  FileDownload, Search, FilterList, Clear, Visibility,
  Description, ShoppingCart, Person, PictureAsPdf,
  Image as ImageIcon, FolderOpen, LocalShipping
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import {
  getRekapPerBantuan,
  getRekapPerSupplier,
  getRekapPerJenis,
  getRekapPeriode,
  getDetailAnggaranBantuan,
  exportRekapAnggaran
} from '../API/transaksiBantuan';
import { getBantuan } from '../API/transaksiBantuan';
import { getSupplierBarang } from '../API/supplierPenyedia';

const formatRp = (n) => {
  if (!n && n !== 0) return 'Rp 0';
  return 'Rp ' + Number(n).toLocaleString('id-ID');
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const statusColor = (status) => {
  if (status >= 80) return 'success';
  if (status >= 50) return 'warning';
  return 'error';
};

// ============================================================
// KOMPONEN PREVIEW PDF
// ============================================================
const PdfPreviewDialog = ({ open, onClose, fileUrl, fileName, title }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isPdf = fileUrl?.toLowerCase().includes('.pdf');
  const isImage = fileUrl?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i);

  useEffect(() => {
    if (open && fileUrl) {
      setLoading(true);
      setError(null);
      
      const timer = setTimeout(() => setLoading(false), 500);
      return () => clearTimeout(timer);
    }
  }, [open, fileUrl]);

  // Fungsi untuk mendapatkan URL yang benar - gunakan getFileUrl yang sudah diperbaiki
  const getFullUrl = (url) => {
    return getFileUrl(url);
  };

  const fullUrl = getFullUrl(fileUrl);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isPdf ? <PictureAsPdf /> : <ImageIcon />}
          <Typography variant="h6">
            {title || 'Preview File'}
          </Typography>
          {fileName && (
            <Chip 
              size="small" 
              label={fileName} 
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
          <Clear fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            gap: 2
          }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Memuat file...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            p: 4
          }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : isPdf ? (
          <Box sx={{ height: '100%', minHeight: '70vh' }}>
            <iframe
              src={`${fullUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
              width="100%"
              height="100%"
              title="PDF Preview"
              style={{ border: 'none', minHeight: '70vh' }}
              onError={() => setError('Gagal memuat file PDF')}
            />
          </Box>
        ) : isImage ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            p: 2,
            bgcolor: '#f5f5f5'
          }}>
            <img
              src={fullUrl}
              alt={fileName || 'Preview'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 4
              }}
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setError('Gagal memuat gambar');
              }}
            />
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            gap: 2,
            p: 4
          }}>
            <FolderOpen sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant="body1" color="text.secondary">
              Format file tidak didukung untuk preview
            </Typography>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => openInAppFilePreview({ url: fullUrl, title: 'File' })}
            >
              Lihat File
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose}>Tutup</Button>
        {fileUrl && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => openInAppFilePreview({ url: fullUrl, title: 'File' })}
            color="success"
          >
            Lihat File
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
const getFileUrl = (fileUrl) => {
  if (!fileUrl) return '';
  if (fileUrl.startsWith('http')) return fileUrl;
  
  // Untuk file RAB (sudah include folder)
  if (fileUrl.startsWith('rab/')) {
    return `${process.env.REACT_APP_URL}/files/${fileUrl}`;
  }
  
  // Untuk file supplier (fotoKwitansi, fotoBarang)
  if (fileUrl.startsWith('supplier_')) {
    return `${process.env.REACT_APP_URL}/files/images/${fileUrl}`;
  }
  
  // Untuk file bantuan (foto_kwitansi upah tukang)
  if (fileUrl.startsWith('bantuan_')) {
    return `${process.env.REACT_APP_URL}/files/images/${fileUrl}`;
  }
  
  if (fileUrl.startsWith('kwitansi_upah_')) {
    return `${process.env.REACT_APP_URL}/files/kwitansi-upah/${fileUrl}`;
  }
  
  // Untuk file images
  if (fileUrl.startsWith('images/')) {
    return `${process.env.REACT_APP_URL}/${fileUrl}`;
  }
  
  // Default - coba uploads/images
  return `${process.env.REACT_APP_URL}/files/images/${fileUrl}`;
};
// ============================================================
// KOMPONEN FILE LIST
// ============================================================
const FileList = ({ files, title, onPreview }) => {
  if (!files || files.length === 0) return null;

  const getFileIcon = (filename) => {
    if (!filename) return <Description color="action" />;
    if (filename.toLowerCase().includes('.pdf')) return <PictureAsPdf color="error" />;
    if (filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i)) return <ImageIcon color="primary" />;
    return <Description color="action" />;
  };

  const getFileTypeLabel = (filename) => {
    if (!filename) return 'File';
    if (filename.toLowerCase().includes('.pdf')) return 'PDF';
    if (filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i)) return 'Gambar';
    return 'File';
  };

  // Perbaiki fungsi getFileUrl menggunakan fungsi global
  const getFullUrl = (fileUrl) => {
    return getFileUrl(fileUrl);
  };

  return (
    <Box sx={{ mb: 2 }}>
      {title && (
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      <Paper variant="outlined" sx={{ bgcolor: '#fafafa', overflow: 'hidden' }}>
        <List dense disablePadding>
          {files.map((file, index) => {
            const fileName = file.nama || file.fileUrl?.split('/').pop() || file;
            const fileUrl = file.fileUrl || file;
            
            return (
              <ListItem
                key={file.id || index}
                divider={index < files.length - 1}
                sx={{
                  flexWrap: 'wrap',
                  gap: 1,
                  py: 1
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {getFileIcon(fileUrl)}
                </ListItemIcon>
                <ListItemText
                  sx={{ flex: '1 1 150px' }}
                  primary={
                    <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 120, sm: 200, md: 300 } }}>
                      {fileName}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {getFileTypeLabel(fileUrl)} • {file.tanggal ? formatDate(file.tanggal) : ''}
                    </Typography>
                  }
                />
                <Box sx={{ 
                  display: 'flex', 
                  gap: 0.5,
                  flexShrink: 0,
                  ml: { xs: 'auto', sm: 0 }
                }}>
                  <Tooltip title="Preview">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onPreview({ ...file, fileUrl: getFullUrl(fileUrl) })}
                      sx={{ p: 0.5 }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Paper>
    </Box>
  );
};

// ============================================================
// KOMPONEN STAT CARD
// ============================================================
const StatCard = ({ icon, label, value, subtitle, color, loading }) => (
  <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{
          bgcolor: `${color}.light`,
          color: `${color}.main`,
          borderRadius: 2,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <Typography variant="h5" fontWeight={700}>{value}</Typography>
          )}
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// ============================================================
// KOMPONEN FILTER BAR
// ============================================================
const FilterBar = ({ filters, onChange, onReset, onApply, loading }) => {
  const [showFilters, setShowFilters] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  const tahunOptions = [];
  for (let i = 2026; i >= 2020; i--) {
    tahunOptions.push(i);
  }

  const bulanOptions = [
    { value: 0, label: 'Semua Bulan' },
    { value: 1, label: 'Januari' },
    { value: 2, label: 'Februari' },
    { value: 3, label: 'Maret' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mei' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'Agustus' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Desember' }
  ];

  return (
    <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList color="action" />
          <Typography variant="subtitle2" fontWeight={600}>Filter</Typography>
          {filters.tahun && (
            <Chip 
              size="small" 
              label={`Tahun: ${filters.tahun}`} 
              onDelete={() => onChange('tahun', '')}
              color="primary"
              variant="outlined"
            />
          )}
          {filters.bulan > 0 && (
            <Chip 
              size="small" 
              label={`Bulan: ${bulanOptions.find(b => b.value === filters.bulan)?.label || filters.bulan}`}
              onDelete={() => onChange('bulan', 0)}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Sembunyikan' : 'Tampilkan'}
          </Button>
          <Button 
            size="small" 
            variant="contained" 
            startIcon={<Refresh />}
            onClick={onApply}
            disabled={loading}
          >
            Terapkan
          </Button>
        </Box>
      </Box>

      {showFilters && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Tahun</InputLabel>
                <Select
                  value={filters.tahun || ''}
                  onChange={(e) => onChange('tahun', e.target.value)}
                  label="Tahun"
                >
                  <MenuItem value="">Semua Tahun</MenuItem>
                  {tahunOptions.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Bulan</InputLabel>
                <Select
                  value={filters.bulan || 0}
                  onChange={(e) => onChange('bulan', Number(e.target.value))}
                  label="Bulan"
                >
                  {bulanOptions.map((b) => (
                    <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button 
                size="small" 
                variant="outlined" 
                startIcon={<Clear />}
                onClick={() => {
                  onChange('tahun', '');
                  onChange('bulan', 0);
                }}
                fullWidth
              >
                Reset Filter
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

// ============================================================
// KOMPONEN DETAIL MODAL (DIPERBARUI)
// ============================================================
// RekapTransaksiBantuan.jsx - DetailAnggaranModal yang diperbaiki

// Helper chip untuk preview file
const FileChip = ({ fileUrl, label, onPreview, id }) => {
  if (!fileUrl) return null;
  const isPdf = String(fileUrl).toLowerCase().includes('.pdf');
  return (
    <Paper elevation={0} sx={{
      display: 'flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.5, borderRadius: 2, bgcolor: '#fafafa',
      border: '1px solid #e0e0e0', cursor: 'pointer',
      '&:hover': { borderColor: '#1976d2', bgcolor: '#f5f5f5' },
    }}
    onClick={() => onPreview({ fileUrl, nama: `${label} #${id || ''}`.trim() })}
    >
      {isPdf ? <PictureAsPdf sx={{ fontSize: 16, color: '#d32f2f' }} /> : <ImageIcon sx={{ fontSize: 16, color: '#1976d2' }} />}
      <Typography variant="caption" sx={{ fontWeight: 500 }}>{label}</Typography>
      <Visibility sx={{ fontSize: 13, color: '#757575' }} />
    </Paper>
  );
};

// ============================================================
// KOMPONEN DETAIL MODAL (DIPERBAIKI)
// ============================================================
const DetailAnggaranModal = ({ open, onClose, BantuanId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    if (open && BantuanId) {
      fetchDetail();
    }
  }, [open, BantuanId]);

  const fetchDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDetailAnggaranBantuan(BantuanId);
      setData(res.data.data);
    } catch (err) {
      console.error(err);
      setError('Gagal memuat detail anggaran');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  // Helper untuk mengekstrak file dari berbagai sumber
  const extractFiles = (items, fileKey = 'fileUrl') => {
    if (!items) return [];
    return items
      .filter(item => item[fileKey])
      .map(item => ({
        id: item.id,
        fileUrl: item[fileKey],
        nama: item.nama || item.keterangan || item.fileUrl?.split('/').pop() || 'File',
        tanggal: item.tanggal || item.createdAt || null,
        ...item
      }));
  };

  // Fungsi untuk mendapatkan URL yang benar
const getFileUrl = (fileUrl) => {
  if (!fileUrl) return '';
  if (fileUrl.startsWith('http')) return fileUrl;
  if (fileUrl.startsWith('rab/')) return `${process.env.REACT_APP_URL}/files/${fileUrl}`;
  if (fileUrl.startsWith('supplier_')) return `${process.env.REACT_APP_URL}/files/images/${fileUrl}`;
  if (fileUrl.startsWith('bantuan_')) return `${process.env.REACT_APP_URL}/files/images/${fileUrl}`;
  if (fileUrl.startsWith('images/')) return `${process.env.REACT_APP_URL}/${fileUrl}`;
  // batch pengiriman files
  if (fileUrl.startsWith('batch_')) return `${process.env.REACT_APP_URL}/files/pengiriman-batch/${fileUrl}`;
  return `${process.env.REACT_APP_URL}/files/images/${fileUrl}`;
};

const PENGIRIMAN_COLOR = { 'Menunggu Respon': 'default', 'Diproses': 'info', 'Dikirim': 'warning', 'Sampai': 'success' };
const BAYAR_COLOR = { 'Belum Dibayar': 'error', 'Sudah Dibayar': 'success' };
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Detail Anggaran Bantuan</Typography>
            <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
              <Clear fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : data ? (
            <Box sx={{ py: 2 }}>
              {/* Header Bantuan */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
                    <Typography variant="caption" color="text.secondary">ID Bantuan</Typography>
                    <Typography variant="h6">#{data.bantuan.id}</Typography>
                    <Typography variant="body2">{data.bantuan.JenisBantuan}</Typography>
                    <Typography variant="body2">Nama Penerima Bantuan :{data.pemohon?.nama || '-'}</Typography>
                    <Typography variant="body2">Alamat Penerima Bantuan : {data.pemohon?.alamat || '-'}</Typography>
                    <Typography variant="body2">Desa/Kelurahan : {data.pemohon?.desaKelurahan || '-'}</Typography>
                    <Typography variant="body2">Kecamatan : {data.pemohon?.kecamatan || '-'}</Typography>

                    <Typography variant="caption" color="text.secondary">
                      TFL: {data.bantuan.namaTFL || '-'} | AGR: {data.bantuan.namaAgr || '-'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
                    <Typography variant="caption" color="text.secondary">Status Anggaran</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Terpakai</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {data.anggaran.persentaseTerpakai}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(data.anggaran.persentaseTerpakai, 100)}
                        color={statusColor(data.anggaran.persentaseTerpakai)}
                        sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Ringkasan Anggaran */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Total Anggaran</Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                        {formatRp(data.anggaran.totalAnggaran)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#e0f2fe', height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Nominal Material</Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="info.main">
                        {formatRp(data.anggaran?.nominalMaterial ?? data.bantuan?.nominalMaterial ?? 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#f3e8ff', height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Nominal Upah Kerja</Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#7c3aed' }}>
                        {formatRp(data.anggaran?.nominalUpahKerja ?? data.bantuan?.nominalUpahKerja ?? 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Total Pembelian</Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="warning.main">
                        {formatRp(data.anggaran.totalPembelian)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#fef3c7', height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Total Upah Tukang</Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="warning.dark">
                        {formatRp(data.anggaran?.totalUpahTukang || 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Card sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Sisa Anggaran</Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="success.main">
                        {formatRp(data.anggaran.sisaAnggaran)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* ========================================================== */}
              {/* UPAH TUKANG */}
              {/* ========================================================== */}
              {data.upahTukang && data.upahTukang.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Upah Tukang
                  </Typography>
                  
                          {data.upahTukang.map((upah) => (
                    <Card key={upah.id} sx={{ mb: 2, bgcolor: '#f5f7fa' }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Tahap</Typography>
                            <Box>
                              <Chip size="small" label={upah.tahap === 'tahap_1' ? 'Tahap I (30-50%)' : upah.tahap === 'tahap_2' ? 'Tahap II (100%)' : (upah.tahap || '-')}
                                color={upah.tahap === 'tahap_1' ? 'warning' : 'success'} variant="outlined" />
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Nominal Upah</Typography>
                            <Typography variant="body2" fontWeight={600} color="primary.main">
                              {formatRp(upah.totalUpah)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Tukang × Hari</Typography>
                            <Typography variant="body2">
                              {upah.totalTukang || 0} org × {upah.totalHari || 0} hari
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Upah Harian</Typography>
                            <Typography variant="body2">{formatRp(upah.upahHarian || 0)}</Typography>
                          </Grid>
                        </Grid>
                        
                        {upah.keterangan && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">Keterangan</Typography>
                            <Typography variant="body2">{upah.keterangan}</Typography>
                          </Box>
                        )}
                        
                        {upah.fotoKwitansi && upah.fotoKwitansi.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Foto Kwitansi ({upah.fotoKwitansi.length})
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {upah.fotoKwitansi.map((foto) => (
                                <Box key={foto.id} sx={{ position: 'relative' }}>
                                  <img
  src={getFileUrl(foto.foto_kwitansi)}
  alt="Kwitansi"
  style={{
    width: 80,
    height: 80,
    objectFit: 'cover',
    borderRadius: 6,
    border: '1px solid #e0e0e0'
  }}
  onError={(e) => {
    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24"%3E%3Cpath fill="%23ccc" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/%3E%3C/svg%3E';
  }}
/>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      bgcolor: 'rgba(255,255,255,0.8)',
                                      '&:hover': { bgcolor: 'white' },
                                      width: 20,
                                      height: 20
                                    }}
                                    onClick={() => handlePreview({ 
                                      fileUrl: foto.foto_kwitansi, 
                                      nama: `Kwitansi Upah #${upah.id}` 
                                    })}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {/* ========================================================== */}
              {/* RAB FILES */}
              {/* ========================================================== */}
              {data.rab && data.rab.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
                    RAB (Rencana Anggaran Biaya)
                  </Typography>
                  
                  {/* File RAB */}
                  <FileList
                    files={extractFiles(data.rab, 'fileRab')}
                    title="File RAB"
                    onPreview={handlePreview}
                  />

                  {/* Detail RAB */}
                  <TableContainer component={Paper} sx={{ bgcolor: '#f5f7fa', mt: 1, overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: { xs: 200, sm: 300 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Keterangan</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.rab.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell sx={{ wordBreak: 'break-word' }}>
                              {r.keterangan}
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                size="small" 
                                label={r.status} 
                                color={r.status === 'disetujui' ? 'success' : 'warning'}
                                sx={{ minWidth: 70 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* ========================================================== */}
              {/* SUPPLIER */}
              {/* ========================================================== */}
              {data.supplier && data.supplier.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    <Store sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Supplier
                  </Typography>
                  
                  {data.supplier.map((s) => (
                    <Card key={s.id} sx={{ mb: 2, bgcolor: '#f5f7fa' }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{s.nama}</Typography>
                            <Typography variant="caption" color="text.secondary">{s.alamat}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight={600} color="primary.main">
                              {formatRp(s.totalTransaksi)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {s.jumlahPembelian} transaksi
                            </Typography>
                          </Box>
                        </Stack>

                        {/* Items dari supplier ini */}
                        {s.items && s.items.length > 0 && (
                          <Box sx={{ mt: 1, overflowX: 'auto' }}>
                            <Typography variant="caption" color="text.secondary">Item Pembelian:</Typography>
                            <TableContainer component={Paper} size="small" sx={{ mt: 0.5, minWidth: 300 }}>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Nama Barang</TableCell>
                                    <TableCell align="right">Harga</TableCell>
                                    <TableCell align="right">Jumlah</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {s.items.map((item) => (
                                    <TableRow key={item.id}>
                                      <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                          <span>{item.namaBarang}</span>
                                          {item.kategoriPembelian && <Chip size="small" label={item.kategoriPembelian} variant="outlined" color="primary" sx={{ fontSize: 10, height: 20 }} />}
                                        </Stack>
                                      </TableCell>
                                      <TableCell align="right">{formatRp(item.hargaSatuan)}</TableCell>
                                      <TableCell align="right">{item.jumlah}</TableCell>
                                      <TableCell align="right" fontWeight={600}>
                                        {formatRp(item.total)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {/* ========================================================== */}
              {/* PEMBELIAN DETAIL DENGAN FILE */}
              {/* ========================================================== */}
              {data.pembelian && data.pembelian.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                    <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Riwayat Pembelian
                  </Typography>
                  
                  {data.pembelian.map((p) => (
                    <Accordion key={p.id} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, flexWrap: 'wrap' }}>
                          <Chip size="small" label={`#${p.id}`} color="primary" variant="outlined" />
                          <Typography variant="body2">
                            {p.supplier?.nama || 'Supplier'}
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ ml: 'auto' }}>
                            {formatRp(p.totalTransaksi)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(p.tanggal)}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* Status pembayaran & pengiriman */}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1.5, gap: 0.5 }}>
                          {p.statusPengiriman && <Chip size="small" label={`Kirim: ${p.statusPengiriman}`} color={PENGIRIMAN_COLOR[p.statusPengiriman] || 'default'} />}
                          {p.statusPembayaran && <Chip size="small" label={`Bayar: ${p.statusPembayaran}`} color={BAYAR_COLOR[p.statusPembayaran] || 'default'} variant={p.statusPembayaran === 'Sudah Dibayar' ? 'filled' : 'outlined'} />}
                          {p.statusPenagihan === 'Ditagihkan' && <Chip size="small" color="warning" label="Ditagihkan" />}
                        </Stack>

                        {/* File Kwitansi dan Foto Barang */}
                        {(p.fotoKwitansi || p.fotoBarang) && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                              <FolderOpen sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} /> File Pendukung Transaksi
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {p.fotoKwitansi && (
                                <FileChip fileUrl={p.fotoKwitansi} label="Kwitansi" onPreview={handlePreview} id={p.id} />
                              )}
                              {p.fotoBarang && (
                                <FileChip fileUrl={p.fotoBarang} label="Foto Barang" onPreview={handlePreview} id={p.id} />
                              )}
                            </Stack>
                          </Box>
                        )}

                        {/* Batch Pengiriman */}
                        {p.pengirimanBatches && p.pengirimanBatches.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                              <LocalShipping sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                              Pengiriman ({p.pengirimanBatches.length} batch)
                            </Typography>
                            {p.pengirimanBatches.map(batch => (
                              <Paper key={batch.id} variant="outlined" sx={{ p: 1, mb: 1, bgcolor: '#f8f9fa' }}>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ mb: 0.5, gap: 0.5 }}>
                                  <Chip size="small" label={`Batch #${batch.batchKe}`} color="primary" variant="outlined" />
                                  <Chip size="small" label={batch.statusPengiriman} color={PENGIRIMAN_COLOR[batch.statusPengiriman] || 'default'} />
                                  <Chip size="small" label={batch.statusPembayaran} color={BAYAR_COLOR[batch.statusPembayaran] || 'default'} variant={batch.statusPembayaran === 'Sudah Dibayar' ? 'filled' : 'outlined'} />
                                  {batch.statusPenagihan === 'Ditagihkan' && <Chip size="small" color="warning" label="Ditagihkan" />}
                                  <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ ml: 'auto' }}>
                                    {formatRp(batch.totalBatch)}
                                  </Typography>
                                </Stack>
                                {/* Items di batch */}
                                {batch.items && batch.items.length > 0 && (
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 0.5, gap: 0.5 }}>
                                    {batch.items.map((it, i) => (
                                      <Chip key={i} size="small" variant="outlined" label={`${it.namaBarang} (${it.jumlahBarang} ${it.satuan || ''})`} sx={{ fontSize: 10 }} />
                                    ))}
                                  </Stack>
                                )}
                                {/* Foto batch */}
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                                  {batch.fotoBarang && <FileChip fileUrl={batch.fotoBarang} label="Foto Barang" onPreview={handlePreview} id={batch.id} />}
                                  {batch.fotoNota && <FileChip fileUrl={batch.fotoNota} label="Foto Nota" onPreview={handlePreview} id={batch.id} />}
                                  {batch.fotoPengiriman && <FileChip fileUrl={batch.fotoPengiriman} label="Foto Kirim" onPreview={handlePreview} id={batch.id} />}
                                  {batch.fotoSampai && <FileChip fileUrl={batch.fotoSampai} label="Foto Sampai" onPreview={handlePreview} id={batch.id} />}
                                </Stack>
                                {batch.catatanPengiriman && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                    Catatan: {batch.catatanPengiriman}
                                  </Typography>
                                )}
                              </Paper>
                            ))}
                          </Box>
                        )}

                        {/* Detail Items */}
                        <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1 }}>
                          Detail Pembelian:
                        </Typography>
                        <TableContainer component={Paper} size="small" sx={{ overflowX: 'auto' }}>
                          <Table sx={{ minWidth: 300 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Nama Barang</TableCell>
                                <TableCell align="right">Harga</TableCell>
                                <TableCell align="right">Jumlah</TableCell>
                                <TableCell align="right">Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {p.items.map((item) => (
                                <TableRow key={item.id}>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                      <span>{item.namaBarang}</span>
                                      {item.kategoriPembelian && <Chip size="small" label={item.kategoriPembelian} variant="outlined" color="primary" sx={{ fontSize: 9, height: 18 }} />}
                                      {item.dikirim && <Chip size="small" label="Dikirim" color="success" sx={{ fontSize: 9, height: 18 }} />}
                                    </Stack>
                                  </TableCell>
                                  <TableCell align="right">{formatRp(item.hargaSatuan)}</TableCell>
                                  <TableCell align="right">{item.jumlah} {item.satuan || ''}</TableCell>
                                  <TableCell align="right" fontWeight={600}>
                                    {formatRp(item.total)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="info">Tidak ada data</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Tutup</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <PdfPreviewDialog
        open={previewOpen}
        onClose={handleClosePreview}
        fileUrl={previewFile?.fileUrl}
        fileName={previewFile?.nama || previewFile?.fileUrl?.split('/').pop()}
        title={previewFile?.nama || 'Preview File'}
      />
    </>
  );
};
// ============================================================
// KOMPONEN UTAMA (SAMA SEPERTI SEBELUMNYA)
// ============================================================
export const RekapTransaksiBantuan = () => {
  const userRole = useSelector((state) => state.authAdmin?.user?.role);
  const isAdmin = userRole === 'admin';
  const isTFL = userRole === 'TFL';
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [ringkasan, setRingkasan] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    tahun: new Date().getFullYear(),
    bulan: 0
  });
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedBantuanId, setSelectedBantuanId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isMobile = useMediaQuery('(max-width:800px)');

  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRekapPerBantuan({
        tahun: filters.tahun || undefined,
        bulan: filters.bulan || undefined,
        page,
        limit
      });
      setData(res.data.data);
      setRingkasan(res.data.ringkasan);
      setTotalItems(res.data.totalItems || res.data.data?.length || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error('Error fetching rekap:', err);
      notify('Gagal mengambil data rekap', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleApplyFilter = () => {
    fetchData();
  };

  const handleResetFilter = () => {
    setFilters({
      tahun: new Date().getFullYear(),
      bulan: 0
    });
    setPage(1);
    fetchData();
  };

  const handleExport = async () => {
    try {
      const response = await exportRekapAnggaran({
        format: 'csv',
        tahun: filters.tahun || undefined,
        bulan: filters.bulan || undefined
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rekap_anggaran_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      notify('Export berhasil');
    } catch (err) {
      console.error('Error exporting:', err);
      notify('Gagal export data', 'error');
    }
  };

  const handleOpenDetail = (bantuanId) => {
    setSelectedBantuanId(bantuanId);
    setDetailOpen(true);
  };

  const contentStyle = isMobile ? {} : { marginLeft: "250px", marginRight: "20px" };

  return (
    <div className="table-responsive rounded border">
      <div className="main-content" style={contentStyle}>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 3, 
            flexWrap: 'wrap', 
            gap: 2 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Analytics color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h5" fontWeight={700}>
                Rekap Anggaran Penerima Bantuan
              </Typography>
              <Chip 
                size="small" 
                label={userRole || 'Guest'} 
                color={isAdmin ? 'error' : isTFL ? 'warning' : 'default'}
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button 
                variant="outlined" 
                startIcon={<Refresh />} 
                size="small" 
                onClick={fetchData}
                disabled={loading}
              >
                Muat Ulang
              </Button>
              <Button 
                variant="contained" 
                startIcon={<Download />} 
                size="small" 
                onClick={handleExport}
                color="success"
              >
                Export CSV
              </Button>
            </Box>
          </Box>

          {/* Ringkasan Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<HomeWork fontSize="large" />}
                label="Total Bantuan"
                value={ringkasan?.totalBantuan || data?.length || 0}
                color="primary"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<AttachMoney fontSize="large" />}
                label="Total Anggaran"
                value={formatRp(ringkasan?.totalAnggaran || 0)}
                color="info"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<Payments fontSize="large" />}
                label="Total Pembelian"
                value={formatRp(ringkasan?.totalPembelian || 0)}
                color="warning"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<Payments fontSize="large" />}
                label="Total Upah Tukang"
                value={formatRp(ringkasan?.totalUpahTukang || 0)}
                color="warning"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<CheckCircle fontSize="large" />}
                label="Sisa Anggaran"
                value={formatRp(ringkasan?.totalSisa || 0)}
                color="success"
                loading={loading}
              />
            </Grid>
          </Grid>

          {/* Filter */}
          <FilterBar
            filters={filters}
            onChange={handleFilterChange}
            onReset={handleResetFilter}
            onApply={handleApplyFilter}
            loading={loading}
          />

          {/* Tabel Data */}
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead sx={{ bgcolor: '#f5f7fa' }}>
                  <TableRow>
                    <TableCell>No.</TableCell>
                    <TableCell>Nama Penerima Bantuan</TableCell>
                     <TableCell>Desa/Kelurahan</TableCell>
                      <TableCell>Kecamatan</TableCell>
                    <TableCell>Jenis Bantuan</TableCell>
                    <TableCell align="right">Total Anggaran</TableCell>
                    <TableCell align="right">Pembelian</TableCell>
                    <TableCell align="right">Upah Tukang</TableCell>
                    <TableCell align="right">Sisa Anggaran</TableCell>
                    <TableCell align="center">Anggaran Terpakai</TableCell>
                    <TableCell align="center">Aksi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                        <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress size={32} />
                        </TableCell>
                      </TableRow>
                    ) : !data || data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">Tidak ada data</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((item, index) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                           <Typography variant="body2" fontWeight={500}>
                            {item.pemohon?.nama || "-"}
                          </Typography>
                        </TableCell>
                         <TableCell>
                           <Typography variant="body2" fontWeight={500}>
                            {item.pemohon?.desaKelurahan || "-"}
                          </Typography>
                        </TableCell>
                         <TableCell>
                           <Typography variant="body2" fontWeight={500}>
                            {item.pemohon?.kecamatan || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {item.JenisBantuan}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.namaTFL ? `TFL: ${item.namaTFL}` : ''}
                            {item.namaAgr ? ` | AGR: ${item.namaAgr}` : ''}
                          </Typography>
                          {item.tanggalBantuan && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatDate(item.tanggalBantuan)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {formatRp(item.nominal)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="warning.main">
                            {formatRp(item.anggaran.totalPembelian)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.anggaran.jumlahPembelian || 0} transaksi · {item.anggaran.jumlahItem || 0} item
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#b45309' }}>
                            {formatRp(item.anggaran?.totalUpahTukang || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.anggaran?.jumlahUpahTukang || item.upahTukang?.length || 0} data
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main" fontWeight={600}>
                            {formatRp(item.anggaran.sisaAnggaran)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ minWidth: 100 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption">
                                {item.anggaran.persentaseTerpakai}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(item.anggaran.persentaseTerpakai, 100)}
                              color={statusColor(item.anggaran.persentaseTerpakai)}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Lihat Detail Anggaran">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDetail(item.id)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>

            {/* Pagination */}
            {data && data.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                p: 2, 
                flexWrap: 'wrap', 
                gap: 1 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={limit}
                      onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    Hal. {page} dari {totalPages} • {totalItems} data
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<NavigateBefore />}
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    Prev
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    endIcon={<NavigateNext />}
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Detail Modal */}
          <DetailAnggaranModal
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            BantuanId={selectedBantuanId}
          />

          {/* Snackbar */}
          <Snackbar 
            open={snackbar.open} 
            autoHideDuration={4000}
            onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            TransitionComponent={Fade}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert 
              onClose={() => setSnackbar(s => ({ ...s, open: false }))}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </div>
    </div>
  );
};